<template lang="pug">
	section.matrixBody
		template(v-if="rows.length")
			template(v-if="!authorized")
				matrix-row(
					v-for="[key, value] in rows",
					:key="key",
					:data="value",
					:labels="labels",
					:cols="cols")
			matrix-drag(
				v-else-if="sortable",
				v-model="rows",
				v-bind="dragOptions",
				handle=".matrixRow-crudTool--move")
				matrix-row-admin(
					v-for="[key, value] in rows",
					:key="key",
					:data="value",
					:labels="labels",
					:show="showModal",
					:sortable="true",
					:cols="cols")
			template(v-else)
				matrix-row-admin(
					v-for="[key, value] in rows",
					:key="key",
					:data="value",
					:labels="labels",
					:show="showModal",
					:sortable="false",
					:cols="cols")
		.matrix-empty(v-else-if="!rows.length") There are no results for your current filter selection.
		button.matrix-save.btn.btn--secondary(v-if="authorized", @click="doSave") Save
</template>

<script>
import Sort from "@/transitions/Sort";
import Row from "./body/Row";
import RowAdmin from "./body/RowAdmin";
import draggable from "vuedraggable";
import { mapGetters, mapMutations, mapActions } from "vuex";
export default {
  name: "MatrixBody",
  props: ["cols"],
  components: {
    matrixRow: Row,
    matrixDrag: draggable,
    matrixRowAdmin: RowAdmin
  },
  data() {
    return {
      drag: false
    };
  },
  computed: {
    ...mapGetters(["authorized", "results", "computed", "sortable"]),
    rows: {
      get() {
        return Object.entries(this.results);
      },
      set(value) {
        this.updateRows(Object.fromEntries(value));
      }
    },
    labels() {
      return this.computed.headers.map(col => col._display);
    },
    dragOptions() {
      return {
        group: "rows",
        disabled: false,
        ghostClass: "ghost"
      };
    }
  },
  methods: {
    ...mapMutations(["updateRows"]),
    ...mapActions(["save"]),
    async doSave() {
      let id = document.querySelector("#tableId").value;
      await this.save(id);
    },
    showModal(type, data, insertAfter) {
      this.$modal.show("row-editor", { type, data, insertAfter });
    },
    handelExternalLinks() {
      let links = this.$el.querySelectorAll("a[href]");
			let siteHost = window.location.host;
      links.forEach(link => {
        let urlHost = link
          .getAttribute("href")
          .match(/(?:^https?\:)?(?:\/\/)(?:www\.)?([^\/?#]+)(?:[\/?#]|$)/i);
        urlHost = urlHost ? urlHost[1] : siteHost;
        if (siteHost != urlHost) {
          link.setAttribute("target", "_blank");
        }
      });
    }
  },
  mounted() {
		this.handelExternalLinks()
	},
  updated() {
		this.handelExternalLinks()
	}
};
</script>

<style lang="scss">
.matrixRow {
  display: flex;
  flex-direction: column;
  background: $white;
  @media (min-width: 1100px) {
    &:nth-child(even):not(:only-child) {
      background: $mist;
    }
  }
  &.edited {
    &:nth-child(even) {
      background: $warning;
    }
    &:nth-child(odd) {
      background: lighten($warning, 10%);
    }
  }
  &.added {
    &:nth-child(even) {
      background: lighten($success, 10%);
    }
    &:nth-child(odd) {
      background: lighten($success, 15%);
    }
  }
}
.matrixRow-inner {
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.15), 0 0 15px rgba(0, 0, 0, 0.05);
  margin: 20px;
  @media (min-width: 1100px) {
    display: grid;
    grid-template-rows: 1fr;
    grid-gap: 10px;
    box-shadow: none;
    margin: 0;
    padding: 0 1rem;
  }
}

.matrixCell-content {
  * {
    font-size: 14px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    padding: 0;
    list-style: none;
  }
  ul {
		ul {
			padding-left: 12px;
			li {
				&::before {
					background: transparent;
					border: 1px solid currentColor;
				}
			}
		}
    li {
      position: relative;
			margin-bottom: 5px;
			text-indent: 10px;
      &::before {
        content: "";
				position: absolute;
				top: 1px;
				left: 0;
        min-height: 5px;
        min-width: 5px;
        max-height: 5px;
        max-width: 5px;
        border-radius: 50%;
        background: currentColor;
        align-self: flex-start;
        margin-top: 8px;
        margin-right: 8px;
      }
    }
  }
}

.matrixCell {
  position: relative;
  display: flex;
  padding: 0.5rem 0.75rem;
  &:nth-child(even) {
    background: $mist;
  }
  &:first-child:not(:only-child) {
    background: $cobalt;
    color: $white;
    font-size: 18px;
    font-weight: 700;
    &::before {
      display: none;
    }
  }
  &:nth-child(even):not(:only-child) {
    background: $mist;
  }
  &[data-label]:before {
    content: attr(data-label) ":";
    margin-right: 10px;
    font-weight: 700;
    font-size: 0.875rem;
		white-space: nowrap;
		min-width: 100px;
		flex: 0 1 auto;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.5;
		@media (min-width: 500px) {
			min-width: 150px;
		}
		@media (min-width: 750px) {
			min-width: 250px;
		}
  }
  @media (min-width: 1100px) {
    padding: 0.5rem 0;
    color: $black;
    &:nth-child(even),
    &:first-child:not(:only-child),
    &:nth-child(even):not(:only-child) {
      background: none;
      color: $black;
    }
    &::before {
      display: none;
    }
  }
}

.matrixCell-content {
  font-size: 0.875rem;
  @media (min-width: 1100px) {
    font-weight: 500;
  }
}
</style>

<style lang="scss" scoped>
.matrixBody {
  display: flex;
  flex-direction: column;
  position: relative;
  &[data-tag="header"] {
    z-index: 2;
    background: $white;
    @media (min-width: 1100px) {
      padding-top: 1.5rem;
      top: 0;
      position: sticky;
    }
  }
  &.header {
    background: #ae8b3a;
  }
}

.rearrange-move {
  transition: transform 0.5s;
}

.matrix-empty {
  padding: 3rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: "";
    border-left: 5px solid $empire;
    margin-right: 10px;
    height: 2rem;
  }
}

.matrix-save {
  margin-top: 20px;
  align-self: flex-end;
}
</style>